import React from 'react';
import PropTypes from 'prop-types';

// WebAwesome.
import WaSkeleton from '@web.awesome.me/webawesome-pro/dist/react/skeleton';

// React-shared (the real DataManager header and mobile-controls chrome, so
// skeletons are pixel-identical to the loaded ones by construction).
import {
  Header as DataManagerHeader,
  Actions as DataManagerActions,
} from '@exonet/react-shared/lib/data-manager/components/HeaderSection/styled';
import {
  MobileButtons,
  MobileFilters,
  MobileExportWrapper,
} from '@exonet/react-shared/lib/data-manager/components/ControlSection/styled';
import { Export as ExportTrigger } from '@exonet/react-shared/lib/data-manager/components/ControlSection/components/Export/components/Trigger/styled';

// Components.
import Badge from 'app/client/components/Badge';
import TranslatedTable from 'app/client/components/Table';

// Styled.
import * as S from './styled';

// Match the DataManager select's rendered height exactly. The select's
// minHeight already includes its border (global border-box box-sizing), so use
// the height var as-is — adding the border would make the skeleton 2px taller
// than the real control and shift the table on load.
const FIELD_HEIGHT = 'var(--data-manager-select-height, 40px)';

// The search field is a plain <input> sized by line-height + vertical padding
// + border (auto height includes the border), so match that exactly.
const SEARCH_HEIGHT =
  'calc(var(--font-size, 1rem) * var(--line-height, 1.5) + var(--xs-padding-vertical, 0.5rem) * 2 + var(--form-input-border-width, 1px) * 2)';

/**
 * A loading boundary for DataManager-based overview pages.
 *
 * Renders the given `skeleton` placeholder while the wrapped DataManager shows
 * only its spinner, then swaps to the real content once it has mounted. The
 * switch is purely CSS-driven (see styled `Boundary`), so no loading state has
 * to be threaded through.
 *
 * Compose the `skeleton` from the exported primitives plus a
 * `<Table … skeleton />`, e.g.:
 *
 *   <OverviewSkeleton
 *     skeleton={(
 *       <>
 *         <SkeletonTitle />
 *         <SkeletonControls><SkeletonField /><SkeletonSearch /></SkeletonControls>
 *         <TranslatedTable columns={columns(t)} skeleton />
 *       </>
 *     )}
 *   >
 *     <DataManager … />
 *   </OverviewSkeleton>
 *
 * @param {node} skeleton The loading placeholder shown during the initial mount.
 * @param {node} children The DataManager (and anything else) to render.
 *
 * @returns {Element} The rendered boundary.
 */
const OverviewSkeleton = ({ skeleton, children }) => (
  <S.Boundary>
    <S.Fallback>{skeleton}</S.Fallback>
    {children}
  </S.Boundary>
);

OverviewSkeleton.propTypes = {
  skeleton: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
};

// A placeholder sized like a form control (select/input). Pass `label` to show
// the (static) filter name above it — only the input itself stays a skeleton.
// NOTE: keep the host as flex — wa-skeleton's visible indicator fills the host
// via `flex: 1 1 auto`, so a block host collapses it to zero height.
export const SkeletonField = ({ label = null }) => {
  const input = <WaSkeleton effect="sheen" style={{ width: '200px', height: FIELD_HEIGHT }} />;

  if (!label) {
    return input;
  }

  return (
    <S.LabeledField>
      <S.FieldLabel>{label}</S.FieldLabel>
      {input}
    </S.LabeledField>
  );
};

SkeletonField.propTypes = {
  label: PropTypes.node,
};

// A placeholder for the search input, sized like the real ControlSection
// search (200px wide on desktop next to filters; pass `width` for variants —
// without filters the real search flexes wider, ~240px). Positioning is
// handled by its container (the header pushes it right via the title's flex,
// the Actions group is right-aligned), so it carries no margin of its own.
export const SkeletonSearch = ({ width = '200px' }) => (
  <WaSkeleton effect="sheen" style={{ width, height: SEARCH_HEIGHT }} />
);

SkeletonSearch.propTypes = {
  width: PropTypes.string,
};

// A non-interactive button placeholder. Pass `children` to show a real, static
// label (e.g. an export button); leave empty to reserve a button-sized box
// (e.g. a header create/action button whose label isn't worth replicating).
// `end` pushes it to the right of a controls row.
export const SkeletonButton = ({ children = null, end = false }) => (
  <S.SkeletonButton $end={end}>{children}</S.SkeletonButton>
);

SkeletonButton.propTypes = {
  children: PropTypes.node,
  end: PropTypes.bool,
};

// A 1em block standing in for a FontAwesome icon that lives in react-shared's
// own kit (which the portal doesn't ship), sized like the real icon so the
// label next to it doesn't shift on load.
const SkeletonIcon = () => (
  <WaSkeleton
    effect="sheen"
    style={{ width: '1em', height: '1em', marginRight: 'var(--xs-padding-horizontal)' }}
  />
);

// The export button placeholder: the REAL export trigger chrome with the real
// (static) label — only the icon is a skeleton block (see SkeletonIcon).
export const SkeletonExport = ({ label }) => (
  <ExportTrigger as="div">
    <SkeletonIcon />
    <span>{label}</span>
  </ExportTrigger>
);

SkeletonExport.propTypes = {
  label: PropTypes.node.isRequired,
};

// The header row with the real (already-known) page title and, on the right, a
// search placeholder and/or header action placeholders. Reuses the REAL
// DataManager HeaderSection chrome (same styled PageHeader and Actions
// wrapper), so the skeleton header matches the loaded one by construction.
export const SkeletonHeader = ({ title, withSearch = true, actions = null }) => (
  <DataManagerHeader
    title={title}
    useHelmet={false}
    render={() =>
      withSearch || actions ? (
        <DataManagerActions>
          {withSearch && <SkeletonSearch />}
          {actions}
        </DataManagerActions>
      ) : null
    }
  />
);

SkeletonHeader.propTypes = {
  title: PropTypes.node.isRequired,
  withSearch: PropTypes.bool,
  actions: PropTypes.node,
};

// A skeleton bar for the dynamic part of a heading (e.g. "App - <bar>").
// The wrapper is an inline-block sized by a zero-width space, so the line box
// keeps its exact natural text height; the bar is absolutely positioned inside
// and therefore cannot stretch the line (no layout shift when the real text
// replaces it).
export const SkeletonTitleBar = ({ width = '240px' }) => (
  <span style={{ position: 'relative', display: 'inline-block', width }}>
    {'\u200B'}
    <WaSkeleton
      effect="sheen"
      style={{
        position: 'absolute',
        top: '50%',
        left: 0,
        right: 0,
        height: '1em',
        transform: 'translateY(-50%)',
      }}
    />
  </span>
);

SkeletonTitleBar.propTypes = {
  width: PropTypes.string,
};

// A placeholder for a form input, sized exactly like a real input (same
// padding/line-height/border metrics via SkeletonInputBox).
export const SkeletonInput = () => (
  <S.SkeletonInputBox>
    {'\u200B'}
    <WaSkeleton effect="sheen" style={{ position: 'absolute', inset: 0 }} />
  </S.SkeletonInputBox>
);

// A placeholder for a single value (e.g. the value cell of a label-list row).
// Sized to a text line; pass `width` to vary it per row.
// NOTE: keep the host on its default (flex) display — wa-skeleton's indicator
// fills the host via flex and collapses to zero height on a block host.
export const SkeletonValue = ({ width = '60%' }) => (
  <WaSkeleton effect="sheen" style={{ width, height: '0.85rem' }} />
);

SkeletonValue.propTypes = {
  width: PropTypes.string,
};

// A placeholder for a value that is rendered as a badge (a status pill is taller than a text line,
// so a plain SkeletonValue makes the tile grow when the data lands). Uses the real badge chrome, so
// its height follows the badge itself instead of a hardcoded one.
export const SkeletonBadge = ({ width = '48px' }) => (
  <Badge>
    <WaSkeleton effect="sheen" style={{ width, height: '0.7rem' }} />
  </Badge>
);

SkeletonBadge.propTypes = {
  width: PropTypes.string,
};

// The controls section keeps the pinned height on an outer wrapper while the
// inner row stretches its items (like the real ControlSection), so the search
// in SkeletonActions lines up with the filter selects instead of the section
// bottom. Below the sm breakpoint the row is hidden and — exactly like the
// real ControlSection — only the mobile "Filters" toggle (plus an export
// placeholder when `withExport` is set) is shown, using the real mobile
// chrome, which hides itself on desktop. Pass the (static) export label as
// `withExport` so the mobile placeholder shows the real button text, like the
// desktop export placeholder in SkeletonActions. Pages keep using
// <SkeletonControls>.
export const SkeletonControls = ({ children, withExport = false }) => (
  <S.SkeletonControlsSection>
    <MobileButtons>
      <MobileFilters as="div">
        <SkeletonIcon />
        Filters
      </MobileFilters>
      {withExport && (
        <MobileExportWrapper>
          <SkeletonExport label={withExport} />
        </MobileExportWrapper>
      )}
    </MobileButtons>
    <S.SkeletonControlsRow>{children}</S.SkeletonControlsRow>
  </S.SkeletonControlsSection>
);

SkeletonControls.propTypes = {
  children: PropTypes.node.isRequired,
  withExport: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
};

export { SkeletonCard, SkeletonActions } from './styled';

/**
 * Build a `loader` for boost()'s `loader` option that shows a table skeleton
 * (with the given columns) while a fetcher-backed section/tile is loading,
 * instead of the default spinner. Handy for dashboard tiles and other embedded
 * fetcher tables.
 *
 * @param {Function} columns The columns factory (called with `t`).
 * @param {boolean|number} rows How many skeleton rows to render (a number for an
 *                              exact count, e.g. dashboard tiles showing max 3).
 *
 * @returns {Function} A loader component for boost.
 */
export const tableSkeletonLoader = (columns, rows = true) => {
  const TableSkeletonLoader = ({ t }) => <TranslatedTable columns={columns(t)} skeleton={rows} />;

  TableSkeletonLoader.propTypes = {
    t: PropTypes.func.isRequired,
  };

  return TableSkeletonLoader;
};

export default OverviewSkeleton;
